// James Madison University: public college page (#2839), official facts only.
//
// Every figure below was read on 2026-10-01 from the source it names:
//  - JMU's Common Data Set 2025-2026 (Planning, Analytics and Institutional
//    Research; posted as a Word document, the newest edition on the CDS
//    page): C1 applicants, admits and enrollees for Fall 2025 with the
//    residency table (the Unknown column is blank and left out), C8F test
//    policy, C21/C22 plans. C21: no early decision plan. C22: a nonbinding,
//    not restrictive early action plan, with no counts (the CDS asks none).
//  - JMU Undergraduate Admissions: Dates and Deadlines and First-Year
//    Applicants (the same first-year deadlines table, test policy, Honors
//    College essays, the arts application). Departure: neither page names
//    the entry term; both were updated July 30, 2026, and the CDS C22 early
//    action dates (closing November 1) match, so they are read as the fall
//    2027 cycle.
//  - JMU College of Visual and Performing Arts, Arts Admissions: the extra
//    application, portfolio and audition steps, with dates for fall 2027.
//  - JMU Office of Financial Aid and Scholarships, Cost of Attendance,
//    2026-2027 Annual Costs. Departure: the CDS G1 box is checked "not
//    available at this time" for 2026-2027 costs, so the aid office's
//    budget is used. JMU lists tuition and fees as one line; its tuition is
//    a calculated average that includes some course and program fees. The
//    items sum to JMU's own totals.
//  - College Scorecard API, school id 232423 (net price by family income,
//    in-state).
// Nothing here is profile-derived, an odds figure or a student record.
// Re-check a figure against its source before editing it, and move
// `lastChecked` when you do.

export const SOURCES = {
    cds: {
        label: 'JMU Common Data Set 2025-2026',
        url: 'https://www.jmu.edu/pair/ir/common-data-set/index.shtml',
        read: '2026-10-01',
    },
    deadlines: {
        label: 'JMU Undergraduate Admissions, Dates and Deadlines',
        url: 'https://www.jmu.edu/admissions/apply/dates-and-deadlines.shtml',
        read: '2026-10-01',
    },
    firstYear: {
        label: 'JMU Undergraduate Admissions, First-Year Applicants',
        url: 'https://www.jmu.edu/admissions/apply/first-year/index.shtml',
        read: '2026-10-01',
    },
    arts: {
        label: 'JMU College of Visual and Performing Arts, Arts Admissions',
        url: 'https://www.jmu.edu/arts/admissions/index.shtml',
        read: '2026-10-01',
    },
    coa: {
        label: 'JMU Office of Financial Aid and Scholarships, Cost of Attendance',
        url: 'https://www.jmu.edu/financialaid/learn/cost-of-attendance-undergrad.shtml',
        read: '2026-10-01',
    },
    scorecard: {
        label: 'College Scorecard, U.S. Department of Education',
        url: 'https://collegescorecard.ed.gov/school/?232423-James-Madison-University',
        read: '2026-10-15',
    },
};

const college = {
    slug: 'james-madison-university',
    kbId: 'james_madison_university',
    name: 'James Madison University',
    shortName: 'JMU',
    aliases: ['JMU', 'James Madison'],
    location: 'Harrisonburg, VA',
    control: 'Public',
    lastChecked: '2026-10-01',
    sources: SOURCES,
    // From the KB's sourced branding (JMU identity colors); decoration only.
    branding: {
        primary: '#450084',
        secondary: '#CBB677',
        logo_url: 'https://storage.googleapis.com/college-counselor-assets/university-logos/james_madison_university_logo.png',
    },

    admission: {
        entering: 'Fall 2025',
        cdsCycle: '2025-2026',
        applied: 42595,
        admitted: 29300,
        enrolled: 4935,
        source: 'cds',
        where: 'section C1',
        residency: [
            { group: 'In-state', applied: 20933, admitted: 13429 },
            { group: 'Out-of-state', applied: 21513, admitted: 15740 },
            { group: 'International', applied: 149, admitted: 131 },
        ],
        residencySource: 'cds',
        residencyWhere: 'section C1, residency table',
    },
    // Acceptance by school or major: shown only where the school publishes it.
    byMajor: {
        published: false,
        note: 'JMU\'s Common Data Set reports one first-year acceptance rate for the university, with no rate by college or major.',
        source: 'cds',
    },
    earlyRounds: {
        title: 'Early action',
        note: 'JMU has no early decision plan. Its Early Action plan is non-binding and does not limit where else you apply early. The Common Data Set gives no early action counts, so it gives no early round acceptance rate.',
        source: 'cds',
        where: 'sections C21 and C22',
    },

    programs: {
        entering: 'Fall 2027',
        intro: 'Every first-year applicant sends the JMU application and a high school transcript. Some programs ask for more:',
        rows: [
            { school: 'Honors College', requirement: 'Two short essays, written in the JMU application and due by your application deadline. The Honors College review is separate from the admission review.', source: 'firstYear' },
            { school: 'Art, Design and Art History', requirement: 'The Architectural Design, Art and Graphic Design BFA and the Industrial Design BS need an extra SlideRoom application with a portfolio, due November 13, 2026 for Early Action or February 5, 2027 for Regular Decision. The Art BA, Art BS and Art History BA need nothing extra.', source: 'arts' },
            { school: 'Music', requirement: 'The Bachelor of Music and the BA in Music need an extra SlideRoom application. Bachelor of Music applicants audition in person or send a video with a portfolio.', source: 'arts' },
            { school: 'Theatre, Musical Theatre and Dance', requirement: 'An extra SlideRoom application, due nine days before your audition. Auditions are in person or by video.', source: 'arts' },
        ],
        source: 'firstYear',
    },

    deadlines: {
        entering: 'Fall 2027',
        headline: { label: 'Regular Decision', value: 'January 15' },
        rows: [
            { plan: 'Early Action', deadline: 'November 1', decision: 'Mid to late January', binding: false },
            { plan: 'Regular Decision', deadline: 'January 15', decision: 'Mid to late March', binding: false },
        ],
        note: 'Deadlines are for every piece of the application: the online application and your transcripts. JMU does not consider an application that misses its deadline. The application fee is $70. Decisions are posted in the Applicant Center of MyMadison. The enrollment deposit is due May 1 for both plans.',
        source: 'deadlines',
    },

    testing: {
        entering: 'Fall 2027',
        policy: 'Test-optional',
        blind: false,
        line: 'JMU no longer requires SAT or ACT scores. If you choose to send a score, official results must come electronically from the testing agency and arrive by the application deadline.',
        source: 'firstYear',
        cdsLine: 'The Common Data Set also says, for fall 2027 applicants: JMU is test optional.',
        cdsWhere: 'section C8',
    },

    // The next cycle (2027-28, fall 2028 entry): only what the school has
    // announced, read on the date below; nothing announced says so (#2651).
    nextCycle: {
        entering: 'Fall 2028',
        checked: '2026-10-01',
        changes: [],
        source: 'firstYear',
    },

    cost: {
        year: '2026-2027',
        columns: ['In-state', 'Out-of-state'],
        rows: [
            { item: 'Tuition and fees', amounts: [15120, 32970] },
            { item: 'Housing', amounts: [7676, 7676] },
            { item: 'Meals', amounts: [7444, 7444] },
            { item: 'Books and supplies', amounts: [1372, 1372] },
            { item: 'Transportation', amounts: [2368, 2368] },
            { item: 'Personal costs', amounts: [2502, 2502] },
            { item: 'Loan fees', amounts: [76, 76] },
        ],
        totals: [36558, 54408],
        note: 'For a full-time undergraduate, fall and spring. The tuition and fees line is an average that includes some course and program fees, such as College of Business and Nursing differential tuition. The other lines are JMU\'s estimates of what an average full-time undergraduate spends. This budget sets aid eligibility; it is not a bill.',
        source: 'coa',
        netPrice: {
            average: 23550,
            byIncome: [
                { income: '$0 to $30,000', amount: 14379 },
                { income: '$30,001 to $48,000', amount: 14452 },
                { income: '$48,001 to $75,000', amount: 16960 },
                { income: '$75,001 to $110,000', amount: 22031 },
                { income: 'Over $110,000', amount: 31907 },
            ],
            note: 'What first-year, full-time, in-state students who received federal aid paid after grants and scholarships, by family income, in 2024-25, the latest year the Department of Education publishes. Your own price comes from JMU\'s aid offer.',
            tileDetail: 'Per year, after grants, in-state federal aid recipients',
            source: 'scorecard',
        },
    },
};

export default college;
